import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _dec, _class, _Head, _ScreenReaderContent;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component, Children } from 'react';
import { omitProps, callRenderProp } from '@instructure/ui-react-utils';
import { SimpleSelect } from '@instructure/ui-simple-select';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { IconCheckLine } from '@instructure/ui-icons';
import { warn } from '@instructure/console';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';
import TableContext from '../TableContext';

/**
---
parent: Table
id: Table.Head
---
**/
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
let Head = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Head = class Head extends Component {
  /**
   * Returns `true` if the first child's children have a `onRequestSort` prop
   */
  get isSortable() {
    const _ref = Children.toArray(this.props.children),
      _ref2 = _slicedToArray(_ref, 1),
      firstRow = _ref2[0];
    let sortable = false;
    if (firstRow && firstRow.props && firstRow.props.children) {
      Children.forEach(firstRow.props.children, grandchild => {
        var _grandchild$props;
        if (grandchild !== null && grandchild !== void 0 && (_grandchild$props = grandchild.props) !== null && _grandchild$props !== void 0 && _grandchild$props.onRequestSort) {
          sortable = true;
          return;
        }
      });
    }
    return sortable;
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    if (this.isSortable && typeof this.props.renderSortLabel === 'undefined') {
      warn(false, '[Table.Head] The `renderSortLabel` prop should be provided when Table is sortable.');
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }

  /**
   * This `Select` is used in `stacked` layout. It's populated by iterating
   * through the first child's children (by default `ColHeader`) and reading
   * there the `id`, `stackedSortByLabel`, `sortDirection`, `onRequestSort` props
   */
  renderSelect() {
    var _firstRow$props;
    const _this$props3 = this.props,
      children = _this$props3.children,
      renderSortLabel = _this$props3.renderSortLabel;
    const _ref3 = Children.toArray(children),
      _ref4 = _slicedToArray(_ref3, 1),
      firstRow = _ref4[0];
    if (!(firstRow !== null && firstRow !== void 0 && (_firstRow$props = firstRow.props) !== null && _firstRow$props !== void 0 && _firstRow$props.children)) {
      return null;
    }
    const options = [];
    const clickHandlers = {};
    let selectedOption;
    let count = 0;
    Children.forEach(firstRow.props.children, grandchild => {
      count += 1;
      if (!(grandchild !== null && grandchild !== void 0 && grandchild.props)) return; // grandchild can be false
      const _grandchild$props2 = grandchild.props,
        id = _grandchild$props2.id,
        stackedSortByLabel = _grandchild$props2.stackedSortByLabel,
        sortDirection = _grandchild$props2.sortDirection,
        onRequestSort = _grandchild$props2.onRequestSort;
      if (id && onRequestSort) {
        const label = stackedSortByLabel || id;
        options.push({
          id,
          label
        });
        clickHandlers[id] = onRequestSort;
        if (sortDirection !== 'none') {
          selectedOption = id;
        }
      }
    });
    if (!options.length) {
      return null;
    }
    const handleSelect = (event, {
      value
    }) => {
      if (value && typeof clickHandlers[value] === 'function') {
        clickHandlers[value](event, {
          id: `${value}`
        });
      }
    };
    return _jsx("div", {
      role: "rowgroup",
      children: _jsx("div", {
        role: "row",
        children: _jsx("div", {
          role: "cell",
          "aria-colspan": count,
          children: _jsx(SimpleSelect, {
            renderLabel: renderSortLabel ? callRenderProp(renderSortLabel) : _ScreenReaderContent || (_ScreenReaderContent = _jsx(ScreenReaderContent, {})),
            renderBeforeInput: selectedOption && IconCheckLine,
            value: selectedOption,
            onChange: handleSelect,
            children: options.map(({
              id,
              label
            }) => _jsx(SimpleSelect.Option, {
              id: id,
              value: id,
              renderBeforeLabel: id === selectedOption ? IconCheckLine : () => _jsx(IconCheckLine, {
                style: {
                  color: 'transparent'
                }
              }),
              children: label
            }, id))
          })
        })
      })
    });
  }
  render() {
    const _this$props4 = this.props,
      children = _this$props4.children,
      styles = _this$props4.styles;
    return this.context.isStacked ? this.renderSelect() :
    // TODO remove 'hover' exclude in v11, its passed down for compatibility with custom components
    _jsx("thead", {
      ...omitProps(this.props, Head.allowedProps, ['hover']),
      css: styles === null || styles === void 0 ? void 0 : styles.head,
      children: children
    });
  }
}, _Head.displayName = "Head", _Head.componentId = 'Table.Head', _Head.contextType = TableContext, _Head.allowedProps = allowedProps, _Head.propTypes = propTypes, _Head.defaultProps = {
  children: null
}, _Head)) || _class);
export default Head;
export { Head };